import { Button, CircularProgress, Stack , Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { create } from '../../../api/ventura/locationList';
import useAdGroupLocationLists from '../../../hooks/useAdGroupLocationLists';
import { useConfig } from '../../../hooks/useConfig';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { isBlank } from '../../../util/helpers';
import FormEndOptions from '../../forms/FormEndOptions';
import NumericTextField from '../../forms/NumericTextField';
import ServerErrorAlert from '../../shared/ServerErrorAlert';

interface LocationListFormProps {
  locationList: any;
  adGroupId: number;
  adGroupName: string;
  onClose: () => void;
}

const controller = new AbortController();
const { signal } = controller;

function LocationListForm({ locationList, adGroupId, adGroupName, onClose }: LocationListFormProps) {
  const form = useForm({
    defaultValues:
      (locationList && {
        ...locationList,
        location_list_id: locationList.locationListId,
      }),
  });
  const { apiHost } = useConfig();
  const { showSnackBar } = useSnackBar();

  const { refetch } = useAdGroupLocationLists(adGroupId);
  const saveLocationList = useMutation({
    mutationFn: (data: any) => {
      const payload = {
        ...data,
        location_list_id: isBlank(data.location_list_id) ? null : data.location_list_id,
        campaign_id: isBlank(adGroupId) ? null : adGroupId,
      };
      return create(apiHost, adGroupId, payload, signal);
    },
    onSuccess: (data) => {
      if (!data.errors) {
        onClose();
        showSnackBar('Location list saved successfully!', 'success', {
          event: 'location_list_saved',
          ad_group_id: adGroupId,
        });
        refetch();
      }
    },
    onError: () => {
      showSnackBar('Failed to save location list', 'error', {
        event: 'location_list_save_error',
        ad_group_id: adGroupId,
      });
    },
  });

  const submitHandler = (data: any) => {
    saveLocationList.mutate({ ...data, variables: data.variables?.map((v: { value: string }) => v.value) });
  };

  const handleCancelBtnClick = () => {
    controller.abort();
    onClose();
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitHandler)}>
        <Stack alignItems="flex-start" spacing={2} padding={2}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {locationList?.locationListId ? 'Edit' : 'Add'} Location for {adGroupName}
          </Typography>
          <NumericTextField name="location_list_id" label="Location List ID" allowNegative={false} fullWidth />
        </Stack>
        <FormEndOptions>
          <Stack spacing={2}>
            {saveLocationList.isSuccess && saveLocationList.data.errors && (
              <ServerErrorAlert errors={saveLocationList.data.errors} />
            )}
            <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
              <Button variant="outlined" onClick={handleCancelBtnClick}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saveLocationList.isLoading}
                endIcon={saveLocationList.isLoading ? <CircularProgress size="1rem" /> : null}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </FormEndOptions>
      </form>
    </FormProvider>
  );
}

export default LocationListForm;
